"use client"

import { Avatar, Checkbox, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { NovaSession } from "@/types"
import { FC } from "react"
import { ImageSection } from "./ImageSection"

const JobInviteCreativeCardFragment = graphql(`
  fragment JobInviteCreativeCard on IndividualCreative {
    __typename
    id
    handle
    displayName
    professionString
    avatarInitials
    avatar168: avatar(width: 168)
    handle
    locationName

    user {
      id
      name
      connections(type: MUTUAL) {
        totalCount
      }
    }

    ...JobInviteCreativeCardImagesSection
  }
`)

interface Props {
  creative: FragmentType<typeof JobInviteCreativeCardFragment> | undefined | null
  onClick: () => void
  selected: boolean
  serverSession: NovaSession
  trackingEntryPoint: string
  isRecommended: boolean
}

export const JobInviteCreativeCard: FC<Props> = ({
  trackingEntryPoint,
  serverSession,
  onClick,
  selected,
  isRecommended,
  ...props
}) => {
  const creative = useFragment(JobInviteCreativeCardFragment, props.creative)

  return (
    <div className="relative cursor-pointer rounded-lg border border-bg-3 bg-bg-1 py-6" onClick={onClick}>
      {isRecommended ? (
        <Text
          className="absolute left-4 top-0 -translate-y-1/2 rounded-sm bg-primary-5 px-[6px] text-bg-1"
          variant="body3">
          Recommended
        </Text>
      ) : null}
      <div className="mb-5 flex flex-row items-start justify-between px-6">
        <div className="flex flex-row items-center">
          <Avatar initials={creative?.avatarInitials} src={creative?.avatar168} size="xl" />
          <div className="ml-4 flex flex-col">
            <Text variant="body1" maxLines={2}>
              {creative?.displayName || creative?.user.name}
            </Text>
            <Text variant="body3" className="text-fg-2" maxLines={1}>
              {creative?.professionString}
            </Text>
            <div className="flex flex-row items-center">
              <Text variant="body3" className="text-fg-2">
                {creative?.locationName}
              </Text>
              {creative?.user.connections && creative?.user.connections?.totalCount > 0 ? (
                <Text variant="body3" className="text-fg-2">
                  ・{creative?.user.connections?.totalCount} Mutuals
                </Text>
              ) : null}
            </div>
          </div>
        </div>
        <Checkbox checked={selected} checkBoxSize="md" wrapperClassName="pointer-events-none" />
      </div>
      <ImageSection
        creative={creative}
        type="grid"
        containOverflow={false}
        showTextOverlay={false}
        showTextUnder={true}
        serverSession={serverSession}
        entryPoint={trackingEntryPoint}
        imageOpensProject={true}
      />
    </div>
  )
}
